import { ReactElement, useEffect, useState } from 'react';
import { Field, getIn, useFormikContext } from 'formik';
import gridStyles from '@pages/forms/Grid.module.scss';
import { iReviewFormValues } from '@services/ReviewService';
import { Alert, DropdownMenu, FileUploadGroupAccessible, FormLoading, LimitedTextAreaLargeUpdated, TileRadioAccessible } from '@components';
import { FormType } from '@constants';
import { tile } from '@interfaces/interfaces';
import { bytesToMBSize, navToError } from '@utils/index';
import { useLocation, useNavigate } from 'react-router-dom';

import { getValidFilesConfig } from '@api/index';

import InfoIcon from '../../../../images/info.svg';
import styles from '../Step1/ReviewStep1.module.scss';

function ReviewStep8(): ReactElement {
  const { values }: { values: iReviewFormValues; } = useFormikContext<iReviewFormValues>();
  const [circumstances, setCircumstances] = useState('');
  const [TimeExtensionOptionsList, setTimeExtensionOptionsList] = useState([{}]);
  const [loading, setLoading] = useState(true);
  const context = useFormikContext();
  const location = useLocation();
  const navigate = useNavigate();
  const ExceptionalOptionsList = [
    { value: '30 days', display: '30 days' },
    { value: '60 days', display: '60 days' },
  ];
  const SpecialOptionsList = [
    { value: '30 days', display: '30 days' },
    { value: '60 days', display: '60 days' },
    { value: '90 days', display: '90 days' },
  ];

  const YesValue = (): ReactElement => (
    <>
      <div>Yes</div>
    </>
  );

  const NoValue = (): ReactElement => (
    <>
      <div>No</div>
    </>
  );

  const TILE_DATA: tile[] = [
    {
      body: <YesValue />,
      value: 'true',
    },
    {
      body: <NoValue />,
      value: 'false',
    },
  ];

  useEffect(() => {
    if (values.OBLIGATION_NUMBER) {
      setLoading(true);
      Promise.all([
        getValidFilesConfig()
      ])
        .then(([validFilesConfig]) => {
          if (validFilesConfig) {
            const validFileInfo = validFilesConfig.find((item) => item.FormType === FormType.REVIEW);
            context.setFieldValue('FileUploadSizeConfig', bytesToMBSize(validFileInfo?.FileSize || 0));
            context.setFieldValue('FileUploadDescriptionConfig', validFileInfo?.Description);
            context.setFieldValue('FileUploadMaxAttachmentsConfig', validFileInfo?.MaxAttachments);
          }
        })
        .catch((e) => {
          console.log(e);
          navToError(navigate, location);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, []);
  useEffect(() => {
    if (values.HasStatementAndEvidenceSupportingDocs === 'false') {
      context.setFieldValue('StatementAndEvidenceNameField', '');
      context.setFieldValue('StatementAndEvidence', []);
      context.setFieldValue('StatementAndEvidenceProvider', '');
      context.setFieldValue('StatementAndEvidenceDescription', '');
      context.setFieldValue('StatementAndEvidenceSizeErrorHandle', '');
      context.setFieldValue('StatementAndEvidenceFileUploadErrorHandle', '');
      context.setFieldValue('StatementAndEvidenceFileSectionError', '');
    }
  }, [values.HasStatementAndEvidenceSupportingDocs]);

  const UploadSupportingDocsAlert = (
    <div className={styles.alertMessageBox}>
      <b>Without supporting documents, your application may not be successful.</b>
      <p>You should provide all relevant information with your application to help the enforcement agency make a decision.</p>
    </div>
  );
  useEffect(() => {
    if (getIn(values, 'selectTheOption').includes('SelectedGroundInfo1')) {
      setCircumstances('exceptional');
      setTimeExtensionOptionsList(ExceptionalOptionsList);
    }
    if (getIn(values, 'selectTheOption').includes('SelectedGroundInfo4')) {
      setCircumstances('special');
      setTimeExtensionOptionsList(SpecialOptionsList);
    }
  }, []);

  return (
    <>
      {loading && <FormLoading />}
      {!loading && (
      <div className={gridStyles.form}>
        <span className="vfH2">Your statement and evidence</span>
        <p>
          Tell us why you believe your fine&#40;s&#41; should be reviewed.
          <br />
          Make sure you provide plenty of information to support your claim.
        </p>
        <div>
          <Field component={LimitedTextAreaLargeUpdated} name="WhyYourFineShouldBeReviewed" limit={3000} isMandatory />
        </div>
        <Field
          name="HasStatementAndEvidenceSupportingDocs"
          title="Can you upload documents to support your review application?"
          component={TileRadioAccessible}
          tileData={TILE_DATA}
          orientationRow
          isMandatory
        />
        <div className={styles.alertMessageBox}>
          {values.HasStatementAndEvidenceSupportingDocs === 'false' && (
            <Alert icon={InfoIcon} message={UploadSupportingDocsAlert} title="" theme="info" classAlt={styles.boxheight} />
          )}
        </div>
        {values.HasStatementAndEvidenceSupportingDocs === 'true' && (
        <>
          <br />
          <span>
            Attach any supporting documents (maximum of
            {' '}
            {values.FileUploadMaxAttachmentsConfig}
            {' '}
            files; each file should be less than
            {' '}
            {values.FileUploadSizeConfig}
            MB)
          </span>
          <FileUploadGroupAccessible
            uploadAReport="StatementAndEvidenceNameField"
            uploadFieldArrayName="StatementAndEvidence"
            whoPrepared="StatementAndEvidenceProvider"
            briefDescription="StatementAndEvidenceDescription"
            fileSizeErrorField="StatementAndEvidenceSizeErrorHandle"
            fileUploadFailErrorField="StatementAndEvidenceFileUploadErrorHandle"
            UploadFileSectionError="StatementAndEvidenceFileSectionError"
            fileSizelimit={values.FileUploadSizeConfig.toString()}
            totalFileLimit={values.FileUploadMaxAttachmentsConfig}
            hasPrepared={false}
            hasDescribe={false}
            formType={FormType.REVIEW} />
        </>
        )}
        {getIn(values, 'isEnforcementReviewStage') && (circumstances === 'exceptional' || circumstances === 'special') && (
        <>
          <Field
            name="IsMakingTimeRequest"
            title="Would you like to ask for more time to get evidence to support this application?"
            component={TileRadioAccessible}
            tileData={TILE_DATA}
            orientationRow
            isMandatory
          />
          {values.IsMakingTimeRequest === 'true' && (
            <>
              <Field name="TimeExtension" component={DropdownMenu} title="How much time do you need to get evidence?" isMandatory displayText={TimeExtensionOptionsList} defaultOption />
              <p>You will need to provide the additional information by that date. If you do not, the application may be assessed without it.</p>
            </>
          )}
        </>
        )}
      </div>
      )}
    </>
  );
}

export default ReviewStep8;
